import { get } from 'svelte/store';
import { devices } from 'src/stores';
import { isThreeEM, real_id } from 'src/helpers/interface_helpers';
import { convert } from 'src/helpers/utilities';
import { dateFormat } from 'src/helpers/date_helper';
import CacheStatisticRequest from 'src/helpers/statistics/request/chart_cache_statistics_helpers';
import type { Device } from 'src/types/Device';

export type PeakPowerChannel = {
	value: string;
	time: string;
	timestamp: number;
	watts: number;
	consumedWatts: number;
	returnedWatts: number;
	direction: 'import' | 'export';
};

export type PeakPowerDeviceSnapshot = {
	device: Device;
	live: (PeakPowerChannel | null)[];
	avg5: (PeakPowerChannel | null)[];
};

export const mapPeakPowerChannel = (
	entry: { c: number; r: number; t: number } | null | undefined,
): PeakPowerChannel | null => {
	if (!entry) return null;

	const consumed = Number(entry.c ?? 0);
	const returned = Number(entry.r ?? 0);
	const timestamp = Number(entry.t ?? 0);
	const returnedMagnitude = Math.abs(returned);
	const direction = consumed >= returnedMagnitude ? 'import' : 'export';
	const watts = direction === 'import' ? Math.abs(consumed) : returnedMagnitude;

	return {
		value: convert(watts, 'W'),
		time: timestamp > 0 ? dateFormat(timestamp * 1000, 'H:i:s d/m') : '',
		timestamp,
		watts,
		consumedWatts: consumed,
		returnedWatts: returned,
		direction,
	};
};

const mapPeakPowerSeries = (
	series: Array<{ c: number; r: number; t: number } | null | undefined> | null | undefined,
): (PeakPowerChannel | null)[] => (series ?? []).map(mapPeakPowerChannel);

export const getPeakPowerDevices = (): Device[] => {
	const allDevices = get(devices);
	const unique = new Map<string, Device>();
	Object.values(allDevices).forEach((device) => {
		if (!device || !allDevices[real_id(device.id)]?.cloud_options?.measure_peak_power) return;
		const realId = real_id(device.id);
		const multiPhase = isThreeEM(device.type) && device.mode !== 'monophase';
		const key = multiPhase ? realId : `${realId}:${device.channel ?? 0}`;
		const existing = unique.get(key);
		if (!existing || device.id === realId) unique.set(key, device);
	});
	return [...unique.values()];
};

export const fetchPeakPowerForDevice = async (
	device: Device,
): Promise<PeakPowerDeviceSnapshot | null> => {
	const id = real_id(device.id);
	const res = await CacheStatisticRequest.getData(
		'/v2/devices/get/',
		{
			//@ts-ignore
			ids: [id],
		},
		undefined,
		false,
		true,
	);

	const peakPower = res?.[0]?.peakPower;
	if (!peakPower) return null;

	return {
		device,
		live: mapPeakPowerSeries(peakPower.live),
		avg5: mapPeakPowerSeries(peakPower.avg5),
	};
};

export const fetchPeakPowerSnapshots = async (): Promise<PeakPowerDeviceSnapshot[]> => {
	const devicesWithPeak = getPeakPowerDevices();
	if (!devicesWithPeak.length) return [];

	const snapshots = await Promise.all(devicesWithPeak.map(fetchPeakPowerForDevice));
	return snapshots.filter(Boolean) as PeakPowerDeviceSnapshot[];
};
